import { Component, inject } from '@angular/core';
import { UserService } from '../../../services/user.service';
import { WorkspaceService } from '../../../services/workspace.service';
import { filter, firstValueFrom } from 'rxjs';
import { UserInterface, WhiteLabelConfig, WorkspaceInterface } from 'voxr-shared';
import { UserProfileComponent } from '../../user/user-profile/user-profile.component';
import { SelectWorkspaceComponent } from '../../workspace/select-workspace/select-workspace.component';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatCardModule } from '@angular/material/card';
import { Router, RouterLink } from '@angular/router';
import { HomeLandingComponent } from "../home-landing/home-landing.component";
import { AppService, BrandInfo } from '../../../services/app.service';
import { BrandLoadingComponent } from "../brand-loading/brand-loading.component";
import { environment } from '../../../../environments/environment';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';

@Component({
  selector: 'app-home',
  standalone: true,
  imports: [
    CommonModule, RouterLink,
    MatButtonModule, MatIconModule, MatCardModule,
    UserProfileComponent, SelectWorkspaceComponent,
    HomeLandingComponent, BrandLoadingComponent
  ],
  templateUrl: './home.component.html',
  styleUrl: './home.component.scss'
})
export class HomeComponent {
  private userService = inject(UserService);
  private app = inject(AppService);
  private workspaceService = inject(WorkspaceService);
  private router = inject(Router);

  user?: UserInterface;
  workspaces?: WorkspaceInterface[];
  incompleteProfile = false;
  resellerMismatch = false;
  brand?: BrandInfo;
  originalReseller?: BrandInfo;
  voxrUrl = environment.app.mainOrigin;
  brandName = this.app.brandName;
  needsAccountOnboarding = false;
  get workspaceLoadFailed() { return this.workspaceService.workspaceLoadFailed; }

  constructor() {
    this.userService.user$.pipe(takeUntilDestroyed()).subscribe(async user => await this.load(user));
    this.workspaceService.workspaces$.pipe(takeUntilDestroyed()).subscribe(workspaces => {
      if (workspaces === undefined || !this.user) return;
      this.workspaces = workspaces;
      if (this.user && !this.loading) void this.load(this.user, true);
    });
  }

  loading = true;
  private loadGeneration = 0;
  private loadedUserId?: string;
  private async load(user?: UserInterface, fromList = false) {
    const generation = ++this.loadGeneration;
    const userId = user?._id?.toString();
    const identityChanged = userId !== this.loadedUserId;
    try {
      if (identityChanged) {
        this.loading = true;
        this.workspaces = undefined;
        this.loadedUserId = userId;
      }
      if (!user) { this.user = undefined; return; }
      this.user = user;

      const profile = this.user?.profile;
      this.incompleteProfile = !this.user.phone || !profile || !profile['firstName'] || !profile['lastName'] || !profile['company'];
      if (!identityChanged && !fromList && this.workspaces !== undefined) return;

      const workspaces = await firstValueFrom(this.workspaceService.workspaces$.pipe(
        filter((value): value is NonNullable<typeof value> => value !== undefined)
      ));
      if (generation !== this.loadGeneration) return;
      if (this.workspaceService.workspaceLoadFailed) return;
      this.workspaces = workspaces;

      this.brand = this.app.brand();
      if (this.brand) {
        this.resellerMismatch = user.resellerId != this.brand?.resellerId && user._id!.toString() != this.brand?.resellerId;
        if (this.resellerMismatch && user.resellerId) {
          this.originalReseller = (await this.app.brandInfoById('reseller', user.resellerId))!;
        }
      } else {
        if (this.user.resellerId) {
          this.brand = await this.app.getResellerByUserId(this.user.resellerId);
          this.resellerMismatch = !!this.brand?.domains?.length;
        }
      }

      if(this.resellerMismatch) return;

      const hasPendingWorkspaceInvitation = !!this.workspaces?.some(ws =>
        !!ws.invitations?.some(inv =>
          !inv.accepted &&
          inv.email?.toLowerCase() === user.email?.toLowerCase()
        )
        || !!ws.members?.some(member =>
          !member.accepted &&
          member.userId?.toString() === user._id?.toString()
        )
      );

      this.needsAccountOnboarding = (
        !this.user.onboarded?.includes('complete') &&
        !hasPendingWorkspaceInvitation &&
        (!workspaces?.some(ws => ws.ownerId != user._id!.toString()) || !!(this.user.onboarded?.length))
      )
      if (this.needsAccountOnboarding && !user.resellerConfig?.isApproved && !user.affiliateConfig?.isApproved) {
        await this.router.navigate(['/account/onboarding']);
        return;
      }
    }
    finally {
      if (generation === this.loadGeneration) this.loading = false;
    }
  }


}
